import { useEffect, useMemo, useState } from 'react'
import { all as getAllIsoCountries } from 'iso-3166-1'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Metric, MetricDataFilters, MetricDataFilterSet, MetricScenarioType } from '../../../hooks/analytics'
import {
  cloneMetricDataFilters,
  getCountryNameByCode,
  normalizeCountryCode,
  sanitizeMetricDataFilters,
  sanitizeMetricDataFilterSet,
} from './metricDataFilters'

type CountryOverrideDraft = {
  id: string
  countryCode: string
  filters: MetricDataFilterSet
}

type CountryOption = {
  code: string
  name: string
  label: string
  searchText: string
}

type FilterSetEditorProps = {
  value: MetricDataFilterSet
  onChange: (next: MetricDataFilterSet) => void
  supportsReturnPeriods: boolean
  title: string
  helperText?: string
}

const SCENARIO_TYPE_OPTIONS: { value: MetricScenarioType; label: string }[] = [
  { value: 'ssp', label: 'SSP' },
  { value: 'rcp', label: 'RCP' },
]

const COUNTRY_OPTIONS: CountryOption[] = getAllIsoCountries()
  .map(country => ({
    code: country.alpha2,
    name: country.country,
    label: `${country.alpha2} - ${country.country}`,
    searchText: `${country.alpha2} ${country.country}`.toLowerCase(),
  }))
  .sort((left, right) => left.label.localeCompare(right.label))

function formatCountryCodeInput(value: string): string {
  const trimmed = value.trim()
  if (/^[A-Za-z]{0,2}$/.test(trimmed)) {
    return trimmed.toUpperCase()
  }

  return value
}

function getMatchingCountryOptions(query: string): CountryOption[] {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) {
    return COUNTRY_OPTIONS.slice(0, 25)
  }

  return COUNTRY_OPTIONS.filter(option => option.searchText.includes(normalizedQuery)).slice(0, 25)
}

function buildCountryOverrideDraft(countryCode = '', filters: MetricDataFilterSet = {}): CountryOverrideDraft {
  return {
    id: `${countryCode || 'country'}-${Math.random().toString(36).slice(2, 10)}`,
    countryCode,
    filters,
  }
}

function FilterSetEditor({ value, onChange, supportsReturnPeriods, title, helperText }: FilterSetEditorProps) {
  const scenarioTypes = value.scenario_type ?? []
  const returnPeriod = value.return_period ?? {}
  const returnPeriodValues = (returnPeriod.values ?? []).map(String)
  const usesSpecificValues = returnPeriodValues.length > 0
  const usesRange = returnPeriod.min != null || returnPeriod.max != null

  const handleScenarioTypeChange = (nextValues: MetricScenarioType[]) => {
    onChange({
      ...value,
      ...(nextValues.length > 0 ? { scenario_type: nextValues } : { scenario_type: undefined }),
    })
  }

  const handleReturnPeriodValuesChange = (nextValues: string[]) => {
    const parsed = Array.from(
      new Set(
        nextValues.map(item => Number.parseInt(String(item), 10)).filter(item => Number.isInteger(item) && item > 0),
      ),
    ).sort((left, right) => left - right)

    onChange({
      ...value,
      return_period: parsed.length > 0 ? { values: parsed } : undefined,
    })
  }

  const handleReturnPeriodRangeChange = (key: 'min' | 'max', rawValue: string) => {
    const parsedValue = rawValue.trim().length > 0 ? Number.parseInt(rawValue, 10) : null
    const parsed = parsedValue != null && Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : undefined
    const current = value.return_period ?? {}
    const next = {
      min: key === 'min' ? parsed : current.min ?? undefined,
      max: key === 'max' ? parsed : current.max ?? undefined,
    }

    onChange({
      ...value,
      return_period: next.min != null || next.max != null ? next : undefined,
    })
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2">{title}</Typography>
          {helperText ? (
            <Typography variant="body2" color="text.secondary">
              {helperText}
            </Typography>
          ) : null}
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel id={`${title}-scenario-type`}>Scenario type</InputLabel>
          <Select
            multiple
            labelId={`${title}-scenario-type`}
            label="Scenario type"
            value={scenarioTypes}
            onChange={event => handleScenarioTypeChange(event.target.value as MetricScenarioType[])}
            renderValue={selected => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {(selected as MetricScenarioType[]).map(option => {
                  const label = SCENARIO_TYPE_OPTIONS.find(item => item.value === option)?.label ?? option.toUpperCase()
                  return <Chip key={option} size="small" label={label} />
                })}
              </Stack>
            )}>
            {SCENARIO_TYPE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={scenarioTypes.includes(option.value)} />
                <Typography sx={{ ml: 1 }}>{option.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {supportsReturnPeriods ? (
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              Return period: use either a list of exact values or a min/max range.
            </Typography>

            <Autocomplete<string, true, false, true>
              multiple
              freeSolo
              options={[]}
              value={returnPeriodValues}
              onChange={(_, nextValues) => handleReturnPeriodValuesChange(nextValues)}
              disabled={usesRange}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={`${title}-${option}-${index}`} />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  label="Specific return periods"
                  placeholder="Type a value and press Enter"
                />
              )}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                size="small"
                type="number"
                fullWidth
                label="Min return period"
                value={returnPeriod.min ?? ''}
                disabled={usesSpecificValues}
                onChange={event => handleReturnPeriodRangeChange('min', event.target.value)}
                inputProps={{ min: 1, step: 1 }}
              />
              <TextField
                size="small"
                type="number"
                fullWidth
                label="Max return period"
                value={returnPeriod.max ?? ''}
                disabled={usesSpecificValues}
                onChange={event => handleReturnPeriodRangeChange('max', event.target.value)}
                inputProps={{ min: 1, step: 1 }}
              />
            </Stack>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            None of the metrics under this hazard use return periods.
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

type Props = {
  open: boolean
  hazardLabel: string
  metrics: Metric[]
  initialFilters?: MetricDataFilters | null
  onClose: () => void
  onSave: (filters: MetricDataFilters | null) => void
}

export default function HazardFilterDialog({ open, hazardLabel, metrics, initialFilters, onClose, onSave }: Props) {
  const supportsReturnPeriods = useMemo(() => metrics.some(metric => metric.uses_return_periods), [metrics])
  const [rootFilters, setRootFilters] = useState<MetricDataFilterSet>({})
  const [countryOverrides, setCountryOverrides] = useState<CountryOverrideDraft[]>([])
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const cleaned = cloneMetricDataFilters(initialFilters)
    setRootFilters({
      ...(cleaned?.scenario_type ? { scenario_type: cleaned.scenario_type } : {}),
      ...(cleaned?.return_period ? { return_period: cleaned.return_period } : {}),
    })
    setCountryOverrides(
      Object.entries(cleaned?.country_overrides ?? {})
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([countryCode, filters]) => buildCountryOverrideDraft(countryCode, filters)),
    )
    setError('')
  }, [initialFilters, open])

  const handleCountryOverrideChange = (overrideId: string, next: Partial<CountryOverrideDraft>) => {
    setCountryOverrides(prev =>
      prev.map(override => (override.id === overrideId ? { ...override, ...next } : override)),
    )
  }

  const handleSave = () => {
    const cleanedRoot = sanitizeMetricDataFilterSet(rootFilters)
    const normalizedOverrides: Record<string, MetricDataFilterSet> = {}
    const seenCountryCodes = new Set<string>()

    for (const override of countryOverrides) {
      const rawCountryCode = override.countryCode.trim()
      const cleanedFilterSet = sanitizeMetricDataFilterSet(override.filters)
      if (!rawCountryCode && !cleanedFilterSet) {
        continue
      }

      const countryCode = normalizeCountryCode(rawCountryCode)
      if (countryCode.length !== 2) {
        setError('Country overrides must use ISO-3166-1 alpha-2 country codes.')
        return
      }
      if (seenCountryCodes.has(countryCode)) {
        setError(`Duplicate country override: ${countryCode}`)
        return
      }

      seenCountryCodes.add(countryCode)
      if (cleanedFilterSet) {
        normalizedOverrides[countryCode] = cleanedFilterSet
      }
    }

    const cleaned = sanitizeMetricDataFilters({
      ...(cleanedRoot?.scenario_type ? { scenario_type: cleanedRoot.scenario_type } : {}),
      ...(cleanedRoot?.return_period ? { return_period: cleanedRoot.return_period } : {}),
      ...(Object.keys(normalizedOverrides).length > 0 ? { country_overrides: normalizedOverrides } : {}),
    })

    onSave(cleaned ?? null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{`${hazardLabel} filters`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* <Alert severity="info">
            These filters apply to every metric in <strong>{hazardLabel}</strong>.
          </Alert> */}

          {error ? <Alert severity="error">{error}</Alert> : null}

          <FilterSetEditor
            title="Global filters"
            helperText="Used for all properties unless a country-specific filter is defined."
            value={rootFilters}
            onChange={next => {
              setRootFilters(next)
              setError('')
            }}
            supportsReturnPeriods={supportsReturnPeriods}
          />

          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2">Country-specific filters</Typography>
                <Typography variant="body2" color="text.secondary">
                  Override specific keys for properties in a given country.
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<AddCircleOutlineIcon fontSize="small" />}
                onClick={() => setCountryOverrides(prev => [...prev, buildCountryOverrideDraft()])}>
                Add country
              </Button>
            </Stack>

            {countryOverrides.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No country overrides configured.
              </Typography>
            ) : null}

            {countryOverrides.map(override => (
              <CountryOverrideCard
                key={override.id}
                override={override}
                supportsReturnPeriods={supportsReturnPeriods}
                onChange={handleCountryOverrideChange}
                onRemove={() => setCountryOverrides(prev => prev.filter(item => item.id !== override.id))}
                onClearError={() => setError('')}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          onClick={() => {
            onSave(null)
            onClose()
          }}>
          Clear
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function CountryOverrideCard({
  override,
  supportsReturnPeriods,
  onChange,
  onRemove,
  onClearError,
}: {
  override: CountryOverrideDraft
  supportsReturnPeriods: boolean
  onChange: (overrideId: string, next: Partial<CountryOverrideDraft>) => void
  onRemove: () => void
  onClearError: () => void
}) {
  const trimmedCountryInput = override.countryCode.trim()
  const normalizedCountryCode = normalizeCountryCode(override.countryCode)
  const resolvedCountryName = trimmedCountryInput.length >= 2 ? getCountryNameByCode(override.countryCode) : null
  const matchingCountryOptions = useMemo(() => getMatchingCountryOptions(trimmedCountryInput), [trimmedCountryInput])
  const hasPotentialCountryMatch = trimmedCountryInput.length === 0 || matchingCountryOptions.length > 0
  const hasCountryInputError = trimmedCountryInput.length > 0 && !normalizedCountryCode && !hasPotentialCountryMatch
  const helperText = hasCountryInputError
    ? 'No matching ISO-3166 country code. Try a code such as "GB" or search by country name.'
    : 'Enter country code or search by name.'
  const countryBadgeLabel =
    normalizedCountryCode && resolvedCountryName ? `${normalizedCountryCode} - ${resolvedCountryName}` : null

  const commitCountryCode = (value: string) => {
    const normalized = normalizeCountryCode(value)
    if (normalized && normalized !== override.countryCode) {
      onChange(override.id, { countryCode: normalized })
      onClearError()
    }
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, backgroundColor: 'grey.50' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <Autocomplete<CountryOption, false, false, true>
            freeSolo
            fullWidth
            autoHighlight
            selectOnFocus
            clearOnBlur={false}
            handleHomeEndKeys
            options={COUNTRY_OPTIONS}
            inputValue={override.countryCode}
            getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
            filterOptions={(_, state) => getMatchingCountryOptions(state.inputValue)}
            onInputChange={(_, nextInputValue, reason) => {
              if (reason === 'reset') {
                return
              }

              onChange(override.id, { countryCode: formatCountryCodeInput(nextInputValue) })
              onClearError()
            }}
            onChange={(_, nextValue) => {
              const nextCountryCode =
                typeof nextValue === 'string'
                  ? normalizeCountryCode(nextValue) || formatCountryCodeInput(nextValue)
                  : nextValue?.code ?? ''

              onChange(override.id, { countryCode: nextCountryCode })
              onClearError()
            }}
            noOptionsText="No matching country codes"
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={option.code} />
                  <Typography variant="body2">{option.name}</Typography>
                </Stack>
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                label="Country code"
                placeholder="GB or start typing a country"
                error={hasCountryInputError}
                helperText={helperText}
                onBlur={() => commitCountryCode(override.countryCode)}
              />
            )}
            sx={{ maxWidth: 320 }}
          />
          {countryBadgeLabel ? (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={countryBadgeLabel}
              sx={{ mt: 0.75, maxWidth: '100%' }}
            />
          ) : null}
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            color="error"
            aria-label={`Remove ${normalizedCountryCode || 'country'} override`}
            onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>

        <FilterSetEditor
          title={`Override for ${resolvedCountryName || normalizedCountryCode || 'country'}`}
          value={override.filters}
          onChange={next => {
            onChange(override.id, { filters: next })
            onClearError()
          }}
          supportsReturnPeriods={supportsReturnPeriods}
        />
      </Stack>
    </Box>
  )
}
